'use client';

import React, { useDeferredValue } from 'react';
import { useEventListener } from '@react-hookz/web';
import { useQuery } from '@tanstack/react-query';
import { IconLoading02 } from 'icon/General/loading-02';
import { IconSearchMd } from 'icon/General/search-md';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next-nprogress-bar';
import { api } from 'orpc/react';
import { Button } from 'ui';
import { isEnvSSR } from 'utils/env';
import { pathPrefixAtom } from '../../../_components/blog-state';
import { SearchDialog } from './search-dialog';
import { SearchGroup } from './search-group';
import { SearchInput } from './search-input';
import { SearchItem } from './search-item';

const SEARCH_DIALOG_EXIT_DURATION_MS = 200;

type Suggestion = {
  id: string;
  title: string;
  coverImage: string;
  url: string;
  excerpt: string;
};

export type SearchMenuProps = {
  subdomain: string;
  suggestions: Suggestion[];
};

export function SearchMenu({ subdomain, suggestions }: SearchMenuProps) {
  const [open, setOpen] = React.useState(false);

  useEventListener(
    isEnvSSR ? null : document,
    'keydown',
    (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((_open) => !_open);
      }
    },
  );

  const [input, setInput] = React.useState('');
  const deferredSearch = useDeferredValue(input);
  const { data: results, isFetching } = useQuery(
    api.blog.search.queryOptions({
      input: {
        text: deferredSearch,
        subdomain,
      },
      enabled: deferredSearch.length > 0,
      refetchOnWindowFocus: false,
    }),
  );
  const hasResults = (results?.length || 0) > 0;
  let list = <></>;
  if (isFetching) {
    list = (
      <div className="flex items-center justify-center px-2.5 py-6 text-md font-medium text-tertiary">
        <IconLoading02 className="me-2 size-5 animate-spin" />
        <span>Loading...</span>
      </div>
    );
  } else if (hasResults) {
    list = (
      <SearchGroup heading="Links">
        {results?.map((r) => {
          return (
            <SuggestionItem
              key={r.id}
              onSelect={() => {
                setOpen(false);
              }}
              s={r}
            />
          );
        })}
      </SearchGroup>
    );
  } else if (results?.length === 0) {
    list = (
      <p className="px-2.5 py-6 text-center text-md font-medium text-tertiary">
        No results found.
      </p>
    );
  } else {
    list = (
      <SearchGroup heading="Suggestions">
        {suggestions?.map((s) => {
          return (
            <SuggestionItem
              key={s.id}
              onSelect={() => {
                setOpen(false);
              }}
              s={s}
            />
          );
        })}
      </SearchGroup>
    );
  }
  const t = useTranslations('Blog.Search');
  return (
    <div style={{ display: 'var(--search-button-display, block)' }}>
      <Button
        aria-label="Search"
        className="w-full justify-start text-md text-secondary sm:w-auto sm:px-2.5 sm:py-1.5"
        onClick={() => {
          setOpen(true);
        }}
        variant="ghost"
      >
        <IconSearchMd className="size-5" />
        <span className="sm:hidden">Search</span>
      </Button>
      <SearchDialog onOpenChange={setOpen} open={open}>
        <SearchInput
          onValueChange={setInput}
          placeholder={t('Placeholder')}
          value={input}
        />
        <div className="h-75 overflow-y-auto overflow-x-hidden">{list}</div>
      </SearchDialog>
    </div>
  );
}

type SuggestionItemProps = {
  s: Suggestion;
  onSelect: () => void;
};

function SuggestionItem({ s, onSelect }: SuggestionItemProps) {
  const router = useRouter();
  const urlPrefix = useAtomValue(pathPrefixAtom);
  return (
    <SearchItem
      onSelect={() => {
        onSelect();
        window.setTimeout(() => {
          router.push(`${urlPrefix}${s.url}`);
        }, SEARCH_DIALOG_EXIT_DURATION_MS);
      }}
    >
      <p className="truncate text-start">{s.title}</p>
      <p className="truncate text-sm font-medium text-tertiary">{s.excerpt}</p>
      <img
        alt="Cover"
        className="row-span-2 max-h-[44px] rounded"
        src={s.coverImage}
      />
    </SearchItem>
  );
}
